import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import Button from '../../ui/Button'
import Badge from '../../ui/Badge'
import Select from '../../ui/Select/Select'
import StatusDot from './StatusDot'
import WarningModal from './WarningModal'
import { categoryBadgeVariant, categoryLabel } from './constants'
import { pluralize } from '../../../utils/textHelpers'

const OPTION_CLASS = 'tw:inline-flex tw:items-center tw:gap-2'

const renderOption = option => (
  <span className={OPTION_CLASS}>
    <StatusDot color={option.color} label={option.label} />
    <Badge variant={categoryBadgeVariant(option.category)}>{categoryLabel(option.category)}</Badge>
  </span>
)
const BODY_CLASS = 'tw:m-0! tw:mb-4! tw:text-sm tw:text-gray-700 tw:text-center'
const NAME_CLASS = 'tw:font-semibold'
const LABEL_CLASS = 'tw:block tw:text-xs tw:font-medium tw:text-gray-950 tw:mb-1'
const ASTERISK_CLASS = 'tw:text-red-500'
const EMPTY_INTRO = 'Aucune alerte n’utilise ce statut pour le moment.'
  + ' Choisissez tout de même un statut de remplacement : une alerte modifiée hors connexion'
  + ' depuis l’app mobile pourrait encore lui être affectée.'

const DeleteStatusModal = ({ isOpen, statusName, issuesCount, options, onCancel, onConfirm }) => {
  const [target, setTarget] = useState(null)
  const confirmDisabled = !target
  const isPlural = issuesCount > 1
  const attachedVerb = isPlural ? 'sont rattachées' : 'est rattachée'
  const transferVerb = isPlural ? 'les transférer' : 'la transférer'

  const handleConfirm = useCallback(() => {
    onConfirm(target ? target.value : null)
  }, [target, onConfirm])

  const intro = issuesCount > 0 ? (
    <p className={BODY_CLASS}>
      <span className={NAME_CLASS}>{pluralize(issuesCount, 'alerte')}</span>
      {` ${attachedVerb} au statut « `}
      <span className={NAME_CLASS}>{statusName}</span>
      {` ». Choisissez le statut vers lequel ${transferVerb} avant de le supprimer.`}
    </p>
  ) : <p className={BODY_CLASS}>{EMPTY_INTRO}</p>

  const footer = (
    <>
      <Button variant="neutral" onClick={onCancel}>Annuler</Button>
      <Button disabled={confirmDisabled} variant="destructive" onClick={handleConfirm}>
        Transférer et supprimer
      </Button>
    </>
  )

  return (
    <WarningModal footer={footer} isOpen={isOpen} title="Supprimer le statut" onClose={onCancel}>
      {intro}
      <label className={LABEL_CLASS}>
        {'Statut de destination '}
        <span className={ASTERISK_CLASS}>*</span>
      </label>
      <Select
        formatOptionLabel={renderOption}
        isSearchable={false}
        menuPortalTarget={document.body}
        options={options}
        placeholder="Choisir"
        value={target}
        onChange={setTarget}
      />
    </WarningModal>
  )
}

DeleteStatusModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  issuesCount: PropTypes.number.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    category: PropTypes.string,
    color: PropTypes.string,
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired
  })),
  statusName: PropTypes.string.isRequired
}

DeleteStatusModal.defaultProps = {
  options: []
}

export default DeleteStatusModal
